import React, { useEffect } from "react";
import styles from "./Exploremore.module.css";

// Placeholder image imports - replace with actual assets
// import heroBannerImg from "../../assets/images/bg4.jpg";
import chatIcon from "../../assets/images/chat.png";
import attendanceIcon from "../../assets/images/attendance.png";
import demoIcon from "../../assets/images/demo.png";
import eventIcon from "../../assets/images/event.png";
import onlineIcon from "../../assets/images/online.png";
import newsIcon from "../../assets/images/news.png";
import mobileImg from "../../assets/images/shape_3.png";
import androidIcon from "../../assets/images/android.png";
import appleIcon from "../../assets/images/apple.png";
import screenshotSchool from "../../assets/images/scl-add.png";
import screenshotReport from "../../assets/images/report.png";
import screenshotStudent from "../../assets/images/add-student.png";
import fingerIcon from "../../assets/images/finger.png";
import uiIcon from "../../assets/images/UI.png";
import paintIcon from "../../assets/images/paint.png";
import adminIcon from "../../assets/images/admin.png";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const ExploreMore = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Smooth scrolling
    });
  }, []); // Empty dependency array ensures this runs only once when component mounts
  /* ── Key Features data ── */
  const keyFeatures = [
    {
      icon: chatIcon,
      title: "Seamless Communication",
      points: [
        "Key stakeholders in a single platform",
        "Standardized templates for routine updates",
        "Multi-modal communication",
      ],
    },
    {
      icon: attendanceIcon,
      title: "Attendance Management",
      points: [
        "Mobile-based attendance",
        "Online leave requisition by parents",
        "Automailers to parents on absence or late to school",
      ],
    },
    {
      icon: demoIcon,
      title: "School Demographics",
      points: [
        "Detailed records maintenance of students & staff",
        "Efficient & secure access to student/staff information",
      ],
    },
    {
      icon: eventIcon,
      title: "Events Calendar",
      points: [
        "Keep track of important events",
        "Share information on various programs – both in school and outside",
        "Get event notifications",
      ],
    },
    {
      icon: onlineIcon,
      title: "Online Admission",
      points: [
        "Online submission of applications",
        "Customized application processing algorithm",
        "Dashboards to management",
      ],
    },
    {
      icon: newsIcon,
      title: "News & Blogs",
      points: [
        "Provide diverse learning experience to students, teachers & parents",
        "Keep abreast about the useful educational & co-curricular events around",
      ],
    },
  ];

  /* ── Showcase sections data ── */
  const showcaseSections = [
    {
      title: "Single or Multi-tenant",
      descriptions: [
        "Robust and modular architecture designed to handle single & multi-tenant implementation.",
        "Administrate singly or collectively a group of schools using differential role rights.",
      ],
      image: screenshotSchool,
    },
    {
      title: "Report Management",
      descriptions: [
        "Generate customized reports from web module using accurate & real time data to measure performance and make informed decisions.",
      ],
      image: screenshotReport,
    },
    {
      title: "Student & Staff Demographics",
      descriptions: [
        "Establish detailed database of Student & Staff.",
        "Import student & staff data in bulk using excel templates.",
        "Readily export data for other administrative uses.",
      ],
      image: screenshotStudent,
    },
  ];

  /* ── Bottom banner features data ── */
  const bottomFeatures = [
    {
      icon: fingerIcon,
      title: "FINGER PRINT LOGIN",
      description:
        "Allows convenient and secure access to iFillip features using in-built biometric authentication on your mobile devices",
    },
    {
      icon: uiIcon,
      title: "SLEEK DESIGN",
      description:
        "Ifillip mobile apps provides enhanced user experience with easy to navigate and visually appealing design accommodating most of the core features of the web application.",
    },
    {
      icon: paintIcon,
      title: "AFFORDABLE CUSTOMIZATION",
      description:
        "Provide a unique brand experience by customizing your institutional logos, colors, themes & other dynamic content in your application",
    },
    {
      icon: adminIcon,
      title: "MULTIPLE ADMIN PROFILES",
      description:
        "Enable permissions or inherited permissions to different admins and other roles to control the entire web application or a subset of features of the web application",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
        Explore iFillip smart features now | Smart School Management System
        </title>
        <meta
          name="description"
          content="Learn about iFillip school management software with smart features like attendance tracking, online admissions, reports, secure communication across stakeholders"
        />
      </Helmet>
      <div className={styles.exploreMorePage}>
        {/* ═══════════════════════════════════════════
          SECTION 1: Hero Banner
      ═══════════════════════════════════════════ */}
        <section className={styles.heroSection}>
          <div className={styles.heroOverlay}>
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-12">
                  <h2 className={styles.heroTitle}>
                    Give a <span className={styles.heroTitleLarge}>FILLIP</span>{" "}
                    to education!
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* ═══════════════════════════════════════════
   SECTION 2: Key Features
═══════════════════════════════════════════ */}
        <section className={styles.keyFeaturesSection}>
          <div className="container">
            <motion.div
              className={styles.sectionHeader}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className={styles.sectionHeading}>KEY FEATURES</h2>
              <div className={styles.headingDivider}></div>
              <p className={styles.sectionSubtext}>
                iFillip provides a compelling platform for educational
                intuitions to improve its efficiency and effectiveness of its
                daily operations. iFillip intuitive features like IOT-based
                attendance and effective communication system are designed to
                enable the stakeholders to focus on the core activity of
                imparting education.
              </p>
            </motion.div>

            {/* Row 1 - First 3 features */}
            <div className={`row ${styles.featuresGrid}`}>
              {keyFeatures.slice(0, 3).map((feature, index) => (
                <motion.div
                  className="col-lg-4 col-md-4 col-sm-6"
                  key={`feature-${index}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={styles.featureCard}>
                    <div className={styles.featureCardHeader}>
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        className={styles.featureCardIcon}
                      />
                      <h3 className={styles.featureCardTitle}>
                        {feature.title}
                      </h3>
                    </div>
                    <div className={styles.featureCardBody}>
                      {feature.points.map((point, pIndex) => (
                        <motion.p
                          className={styles.featurePoint}
                          key={pIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.3,
                            delay: index * 0.1 + pIndex * 0.05,
                          }}
                        >
                          <span className={styles.checkIcon}>&#10004;</span>{" "}
                          {point}
                        </motion.p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Row 2 - Next 3 features */}
            <div className={`row ${styles.featuresGrid}`}>
              {keyFeatures.slice(3, 6).map((feature, index) => (
                <motion.div
                  className="col-lg-4 col-md-4 col-sm-6"
                  key={`feature-${index + 3}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                >
                  <div className={styles.featureCard}>
                    <div className={styles.featureCardHeader}>
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        className={styles.featureCardIcon}
                      />
                      <h3 className={styles.featureCardTitle}>
                        {feature.title}
                      </h3>
                    </div>
                    <div className={styles.featureCardBody}>
                      {feature.points.map((point, pIndex) => (
                        <motion.p
                          className={styles.featurePoint}
                          key={pIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.3,
                            delay: (index + 3) * 0.1 + pIndex * 0.05,
                          }}
                        >
                          <span className={styles.checkIcon}>&#10004;</span>{" "}
                          {point}
                        </motion.p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* ═══════════════════════════════════════════
          SECTION 3: iFillip Mobile (Blue Section)
      ═══════════════════════════════════════════ */}
        <section className={styles.mobileSection}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-5 col-12">
                <div className={styles.mobileImageWrapper}>
                  <img
                    src={mobileImg}
                    alt="iFillip Mobile"
                    className={styles.mobileImage}
                  />
                </div>
              </div>
              <div className="col-md-7 col-12">
                <div className={styles.mobileContent}>
                  <h2 className={styles.mobileTitle}>iFillip Mobile</h2>
                  <h4 className={styles.mobileSubtitle}>
                    Get <strong>ACTIVELY INVOLVED</strong> in your child's
                    progress
                  </h4>
                  <p className={styles.mobileDescription}>
                    iFillip helps to keep track of your child's achievements,
                    open days, PTA meetings, school events, educational trends
                    et al on the move
                  </p>
                  <div className={styles.mobileAppLinks}>
                    <div className={styles.appLink}>
                      <img
                        src={androidIcon}
                        alt="Android"
                        className={styles.appIcon}
                      />
                      <h6 className={styles.appLinkText}>
                        iFillip for Android Users
                      </h6>
                    </div>
                    <div className={styles.appLink}>
                      <img
                        src={appleIcon}
                        alt="Apple"
                        className={styles.appIcon}
                      />
                      <h6 className={styles.appLinkText}>
                        iFillip for Apple Users
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* ═══════════════════════════════════════════
          SECTION 4: Showcase Sections (Screenshots)
      ═══════════════════════════════════════════ */}
        <section className={styles.showcaseSection}>
          <div className="container">
            {showcaseSections.map((section, index) => (
              <div className={styles.showcaseBlock} key={`showcase-${index}`}>
                <div className="row align-items-center">
                  <div className="col-md-7 col-12">
                    <h2 className={styles.showcaseTitle}>{section.title}</h2>
                    {section.descriptions.map((desc, dIndex) => (
                      <p className={styles.showcaseDescription} key={dIndex}>
                        {desc}
                      </p>
                    ))}
                  </div>
                  <div className="col-md-5 col-sm-6 col-12">
                    <div className={styles.showcaseImageWrapper}>
                      <img
                        src={section.image}
                        alt={section.title}
                        className={styles.showcaseImage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        {/* ═══════════════════════════════════════════
          SECTION 5: Bottom Banner (Glassmorphism)
      ═══════════════════════════════════════════ */}
        <section className={styles.bottomBannerSection}>
          <div className={styles.bottomBannerOverlay}>
            <div className="container">
              <div className="row">
                {bottomFeatures.map((feature, index) => (
                  <div className="col-md-3 col-12" key={`bottom-${index}`}>
                    <div className={styles.bottomFeatureBox}>
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        className={styles.bottomFeatureIcon}
                      />
                      <h4 className={styles.bottomFeatureTitle}>
                        {feature.title}
                      </h4>
                      <p className={styles.bottomFeatureDescription}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ExploreMore;
